import { AppBar, Box, Divider, IconButton, Toolbar, Typography, useTheme } from "@mui/material";
import { useOktaAuth } from "@okta/okta-react";
import type { JSX } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import "./translations";

import { SprengnetterValuationHubValuationPackageModelResponseValuationPackageStatusResponse } from "@/api/generatedApi";
import { LogoutIcon, SprengnetterLogo, SupportIcon, UserCircleIcon } from "@/assets";
import { EnhancedTooltip } from "@/components/enhancedTooltip";
import { SupportMenu, UserMenu } from "@/components/header/menus";
import LanguageSwitcher from "@/components/header/menus/LanguageSwitcher";
import { saveLogoutReturnTo } from "@/helpers/logoutNavigationHelpers";
import { usePermissions } from "@/hooks/permissionsHooks/usePermissions";
import { setUserInfo } from "@/pages/applicationMainSlice";
import { useAppDispatch } from "@/store/hooks";
import { RoleType } from "@/types/RoleType";

const styles = {
  quotaChip: {
    display: "inline-flex",
    alignItems: "center",
    px: 1.5,
    py: 1,
    mr: 1,
    border: "1px solid",
    borderColor: "primary.main",
    color: "primary.main",
    fontWeight: 600,
    fontSize: "14px",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
  },
  divider: {
    borderColor: "primary.main",
    height: 39,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    height: "100%",
    padding: "13px 21px",
    gap: "0px",
    justifyContent: "space-between",
    width: "100%",
  },
};

interface HeaderWithQuotaProps {
  quotaStatus?: SprengnetterValuationHubValuationPackageModelResponseValuationPackageStatusResponse;
  isLoadingQuota?: boolean;
}

export const HeaderWithQuota = ({ quotaStatus }: HeaderWithQuotaProps): JSX.Element => {
  const { t } = useTranslation("ValuationListPage");
  const theme = useTheme();
  const [userAnchorEl, setUserAnchorEl] = useState<HTMLElement | null>(null);
  const [supportAnchorEl, setSupportAnchorEl] = useState<HTMLElement | null>(null);
  const { oktaAuth, authState } = useOktaAuth();
  const userPermissions = usePermissions();

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const onLogoutClick = (): void => {
    saveLogoutReturnTo();
    oktaAuth.signOut();
    dispatch(setUserInfo(undefined));
  };

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>): void => {
    setUserAnchorEl(event.currentTarget);
  };

  const handleSupportMenuClick = (event: React.MouseEvent<HTMLElement>): void => {
    setSupportAnchorEl(event.currentTarget);
  };

  const handleCloseUserMenu = (): void => {
    setUserAnchorEl(null);
  };

  const handleCloseSupportMenu = (): void => {
    setSupportAnchorEl(null);
  };

  const handleLogoClick = (): void => {
    navigate("/");
  };

  let quotaValColor = theme.palette.primary.main;
  if (typeof quotaStatus?.remaining_valuations_count === "number") {
    if (quotaStatus.remaining_valuations_count === 0) {
      quotaValColor = theme.palette.error.main;
    } else if (quotaStatus.remaining_valuations_count < 3) {
      quotaValColor = "#FFAE07";
    }
  }

  const isInstitutionAdmin = userPermissions.has(RoleType.INSTITUTION_ADMIN);

  return (
    <>
      <UserMenu
        anchorEl={userAnchorEl}
        open={Boolean(userAnchorEl)}
        onClose={handleCloseUserMenu}
        isInstitutionAdmin={isInstitutionAdmin}
      />
      <SupportMenu anchorEl={supportAnchorEl} open={Boolean(supportAnchorEl)} onClose={handleCloseSupportMenu} />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          boxShadow: "none",
          borderBottom: "1px solid",
          borderColor: (theme) => theme.palette.divider,
        }}
      >
        <Toolbar sx={styles.toolbar}>
          <Box onClick={handleLogoClick} sx={{ display: "flex", alignItems: "center" }}>
            <SprengnetterLogo sx={{ width: "147px", height: "50px", cursor: "pointer" }} />
          </Box>
          {authState?.isAuthenticated && (
            <Box sx={styles.headerRow}>
              {quotaStatus?.authentication_source !== "SHOPWARE" && !!quotaStatus?.booked_valuations_count && (
                <>
                  <EnhancedTooltip
                    content={quotaStatus?.booked_valuations_count ? t("quotaAvailable") : t("quotaDataUnavailable")}
                  >
                    <Box sx={styles.quotaChip}>
                      <Typography component="span" style={{ color: quotaValColor, fontWeight: 600, fontSize: "14px" }}>
                        {`${quotaStatus?.remaining_valuations_count ?? "-"} / ${quotaStatus?.booked_valuations_count ?? "-"}`}
                      </Typography>
                      <span style={{ marginLeft: 4 }}>{t("valuations")}</span>
                    </Box>
                  </EnhancedTooltip>
                  <Divider orientation="vertical" flexItem sx={styles.divider} />
                </>
              )}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <LanguageSwitcher language="de" setLanguage={() => {}} />
                <IconButton onClick={handleSupportMenuClick}>
                  <SupportIcon color="primary" />
                </IconButton>
                <IconButton onClick={handleUserMenuClick}>
                  <UserCircleIcon color="primary" />
                </IconButton>
                <IconButton onClick={onLogoutClick}>
                  <LogoutIcon color="primary" />
                </IconButton>
              </Box>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Toolbar />
    </>
  );
};
